import React, { ReactElement, Ref } from "react";
import {
  ClassNamesConfig,
  GroupBase,
  SelectInstance,
  GetOptionValue,
  GetOptionLabel,
  DropdownIndicatorProps,
  IndicatorSeparatorProps,
  components,
} from "react-select";
import CreatableSelectComponent, {
  CreatableProps,
} from "react-select/creatable";

import {
  OptionType,
  Control,
  MultiValue,
  MultiValueRemove,
  Option,
  Menu,
  MenuList,
  useStyles,
  useClassNames,
  SelectInputClassNameOptions,
  defaultPlaceholder,
  keepLockedChips,
  KeepLockedProps,
} from "./select-input";
import { cn, firstNotUndefined } from "@/lib/utils.ts";
import { useMenuPortal } from "./menu-portal-context";
import { combineRefs } from "@/hooks/combine-refs";
import { CopyPasteProps, useCopyPaste } from "./copy-paste";

export const Input = components.Input;
export const DropdownIndicator = <
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
>(
  _props: DropdownIndicatorProps<Option, IsMulti, Group>,
) => {
  return null;
};
export const IndicatorSeparator = <
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
>(
  _props: IndicatorSeparatorProps<Option, IsMulti, Group>,
) => {
  return null;
};
export interface Accessors<Option> {
  getOptionValue: GetOptionValue<Option>;
  getOptionLabel: GetOptionLabel<Option>;
}

export type SelectInputCreatableProps<
  Value,
  Option extends OptionType<Value> = OptionType<Value>,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = CreatableProps<Option, IsMulti, Group> & {
  classNamesOptions?: SelectInputClassNameOptions;
  createLabel?: string;
} & CopyPasteProps<Value, Option, IsMulti, Group> &
  KeepLockedProps;

const SelectInputCreatableBase = <
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
>(
  props: SelectInputCreatableProps<Value, Option, IsMulti, Group>,
  ref: React.LegacyRef<SelectInstance<Option, IsMulti, Group>>,
) => {
  const {
    styles: _styles = {},
    classNames: _classNames = {},
    classNamesOptions,
    components = {},
    inputValidator,
    delimiter = /[\n\s,]+/g,
    isMulti,
    formatCreateLabel = props.createLabel
      ? (v) => `${props.createLabel} ${v}`
      : (v) => v,
    getNewOptionData = (inputValue: string, _optionLabel: React.ReactNode) =>
      ({
        value: inputValue,
        label: inputValue,
      }) as Option,
    // noOptionsMessage = () => null,
    menuPortalTarget,
    placeholder = defaultPlaceholder,
    closeMenuOnSelect = !props.isMulti,
    keepLocked,
    ...rest
  } = props;
  const instanceId = React.useId();

  const styles = useStyles({
    styles: _styles,
    options: classNamesOptions,
  });

  const classNames = useClassNames({
    defaultClassNames: React.useMemo(
      () =>
        ({
          valueContainer: (_s) =>
            cn(
              "min-h-10 h-full max-h-20 !overflow-y-auto flex items-center flex-wrap",
            ),
        }) as ClassNamesConfig<Option, IsMulti, Group>,
      [],
    ),
    classNames: _classNames,
    options: classNamesOptions,
  });

  const {
    ref: copyPasteRef,
    copy: _copy,
    storeOptions: _storeOptions,
    ...copyPaste
  } = useCopyPaste<Value, Option, IsMulti, Group>({
    ...props,
    getNewOptionData,
  });

  const _menuPortalTarget = useMenuPortal();

  return (
    <CreatableSelectComponent
      {...copyPaste}
      ref={combineRefs(ref, copyPasteRef)}
      instanceId={instanceId}
      unstyled
      components={{
        Control,
        DropdownIndicator,
        IndicatorSeparator,
        // ClearIndicator,
        MultiValue,
        MultiValueRemove,
        Option,
        Menu,
        MenuList,
        ...copyPaste.components,
        ...components,
      }}
      styles={styles}
      classNames={classNames}
      menuPortalTarget={firstNotUndefined(
        menuPortalTarget,
        _menuPortalTarget,
        document.body,
      )}
      placeholder={placeholder}
      closeMenuOnSelect={closeMenuOnSelect}
      delimiter={delimiter as string}
      isMulti={isMulti}
      formatCreateLabel={formatCreateLabel}
      // noOptionsMessage={noOptionsMessage}
      {...rest}
      keepLocked={keepLocked}
      onChange={keepLocked ? keepLockedChips(rest.onChange) : rest.onChange}
    />
  );
};

export const SelectInputCreatable = React.forwardRef(
  SelectInputCreatableBase,
) as <
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
>(
  p: SelectInputCreatableProps<Value, Option, IsMulti, Group> & {
    ref?: Ref<React.LegacyRef<SelectInstance<Option, IsMulti, Group>>>;
  },
) => ReactElement;

export default SelectInputCreatable;
